import React, { createContext, useCallback, useContext } from "react";
import { useLoggedInUser, UserLoginStatus } from "../api/user/user";
import { useNavigate } from "react-router";
import i18n, { getLocalizedPath } from "../i18n";

export type IdentityType = "patient" | "medical_professional";

interface UserContextType {
  user: UserLoginStatus["user"] | null;
  loggedIn: boolean;
  isLoading: boolean;
  refetch: () => void;
  login: () => void;
  logout: () => void;
  signup: (
    email?: string,
    identityType?: IdentityType,
    optInMarketing?: boolean,
    utmSource?: string,
    utmMedium?: string,
    utmCampaign?: string,
    utmContent?: string,
    utmTerm?: string,
  ) => void;
}

const UserContext = createContext<UserContextType | null>(null);

export type AuthPage = "login" | "signup";

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
};

const handleAuthOpen = (page: AuthPage = "login", email?: string) => {
  const locale = i18n.language;
  const basePath = getLocalizedPath("", locale);

  if (page === "login") {
    window.location.href = `${basePath}/login`;
    return;
  }

  // For signup, use POST to pass email if provided
  if (email) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${basePath}/signup`;

    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      console.error("No CSRF token found");
      return;
    }
    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "authenticity_token";
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    const emailInput = document.createElement("input");
    emailInput.type = "hidden";
    emailInput.name = "email";
    emailInput.value = email;
    form.appendChild(emailInput);

    document.body.appendChild(form);
    form.submit();
  } else {
    window.location.href = `${basePath}/signup`;
  }
};

const handleLogout = () => {
  window.location.href = getLocalizedPath("/logout", i18n.language);
};

const createHubspotLead = (
  email: string,
  identityType: IdentityType,
  utmSource?: string,
  utmMedium?: string,
  utmCampaign?: string,
  utmContent?: string,
  utmTerm?: string,
) => {
  // Use fetch with keepalive: true for fire-and-forget that survives page navigation
  // This is more reliable than sendBeacon for JSON payloads as it ensures
  // the Content-Type header is properly set for Rails to parse the body
  fetch("/api/leads", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      identity_type: identityType,
      marketing_consent: true,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_term: utmTerm,
    }),
    keepalive: true, // Ensures request completes even if page navigates away
  }).catch(() => {
    // Silently fail - this is fire-and-forget, we don't want to block signup
  });
};

const handleSignup = (email?: string, identityType?: IdentityType, optInMarketing?: boolean) => {
  // Create HubSpot lead if email provided and user opted in (fire-and-forget)
  if (email && optInMarketing && identityType) {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get("utm_source");
    const utm_medium = params.get("utm_medium");
    const utm_campaign = params.get("utm_campaign");
    const utm_content = params.get("utm_content");
    const utm_term = params.get("utm_term");

    createHubspotLead(
      email,
      identityType,
      utm_source ?? undefined,
      utm_medium ?? undefined,
      utm_campaign ?? undefined,
      utm_content ?? undefined,
      utm_term ?? undefined,
    );
  }

  handleAuthOpen("signup", email);
};

const handleLogin = () => {
  handleAuthOpen("login");
};

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const userQuery = useLoggedInUser();

  const value: UserContextType = {
    user: userQuery.data?.user,
    loggedIn: userQuery.data?.logged_in || false,
    refetch: () => {
      userQuery.refetch();
    },
    isLoading: userQuery.isLoading,
    logout: () => {
      handleLogout();
    },
    login: useCallback(() => {
      if (!userQuery.data?.logged_in && !userQuery.isLoading) {
        handleLogin();
      }
    }, [userQuery.data?.logged_in, userQuery.isLoading]),
    signup: useCallback(
      (email?: string, identityType?: IdentityType, optInMarketing?: boolean) => {
        if (!userQuery.data?.logged_in && !userQuery.isLoading) {
          handleSignup(email, identityType, optInMarketing);
        }
      },
      [userQuery.data?.logged_in, userQuery.isLoading],
    ),
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
